/**
 * AddBusiness — `/anadir-negocio`
 *
 * Autoalta híbrida: formulario completo sin login → email confirmación →
 * cuenta auto + trial 30 días. Source = `self_submitted`.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BACKEND_URL } from "../lib/api";
import GlobalHeader from "../components/layout/GlobalHeader";
import TurnstileWidget from "../components/TurnstileWidget";
import { Storefront, Sparkle, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

export default function AddBusiness() {
  const [meta, setMeta] = useState({ sectors: [] });
  const [form, setForm] = useState({
    name: "", sectors: [], address: "", city: "", province: "",
    postal_code: "", phone: "", whatsapp: "", email: "",
    website_url: "", contact_name: "", description: "",
  });
  const [tToken, setTToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    document.title = "Añade tu negocio gratis · AG-Ecosystem";
    api.get("/marketplace/businesses/form-meta").then(({ data }) => setMeta(data)).catch(() => {});
  }, []);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleSector = (id) => setForm((f) => ({
    ...f, sectors: f.sectors.includes(id) ? f.sectors.filter((x) => x !== id) : [...f.sectors, id],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.sectors.length === 0) { setError("Selecciona al menos un sector."); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/marketplace/businesses/submit", {
        ...form, turnstile_token: tToken, honeypot: "",
      });
      setResult(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "No se pudo enviar. Inténtalo de nuevo.");
    } finally { setSubmitting(false); }
  };

  if (result?.ok) {
    return (
      <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)]">
        <GlobalHeader />
        <main className="max-w-2xl mx-auto px-6 py-20" data-testid="add-business-success">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500/20 border border-emerald-500/40 mb-6">
              <CheckCircle size={28} weight="fill" className="text-emerald-300" />
            </div>
            <h1 className="font-display text-4xl font-bold tracking-tighter">Revisa tu email</h1>
            <p className="text-[var(--ag-text-dim)] mt-4 text-base leading-relaxed">{result.message}</p>
            <p className="text-sm text-[var(--ag-text-dim)] mt-6">¿No te llega? Revisa la carpeta de spam o reinicia el proceso.</p>
            <Link to="/marketplace" className="mt-8 inline-flex items-center gap-2 text-[var(--ag-gold)] hover:underline text-sm">
              Volver al marketplace <ArrowRight size={14} />
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--ag-midnight)] text-[var(--ag-text)]">
      <GlobalHeader />
      <main className="max-w-3xl mx-auto px-6 py-10 md:py-16">
        <div className="mb-10">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--ag-gold)] flex items-center gap-2">
            <Sparkle size={11} weight="fill" /> Auto-alta gratuita
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter mt-3 text-[var(--ag-text)]">
            ¿No encuentras tu negocio?
          </h1>
          <p className="text-[var(--ag-text-dim)] mt-3 leading-relaxed">
            Inscríbelo gratis en menos de 2 minutos. Al confirmar tu email se creará tu cuenta de
            administrador con <strong className="text-[var(--ag-text)]">prueba gratuita de 30 días</strong> de
            todas las funcionalidades.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-6" data-testid="add-business-form">
          {/* Honeypot trampa */}
          <input type="text" name="company_website_hp" tabIndex="-1" autoComplete="off"
                 className="hidden" aria-hidden="true" />

          {/* Nombre + sectores */}
          <div className="ag-card p-6 space-y-5">
            <div>
              <Label>Nombre del negocio *</Label>
              <Input value={form.name} onChange={onChange("name")} required data-testid="ab-name" />
            </div>
            <div>
              <Label>Sectores * (selecciona los que apliquen)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {meta.sectors.map((s) => (
                  <button type="button" key={s.id} onClick={() => toggleSector(s.id)}
                    className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${
                      form.sectors.includes(s.id)
                        ? "border-[var(--ag-gold)] bg-[var(--ag-gold)]/15 text-[var(--ag-gold)]"
                        : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
                    data-testid={`ab-sector-${s.id}`}>
                    {s.label_es}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Breve descripción de los servicios (opcional)</Label>
              <Textarea value={form.description} onChange={onChange("description")} rows={3}
                placeholder="Detailing premium, diagnosis OBD-II, cambio de aceite, pulido cerámico, alineación 4 ruedas…"
                data-testid="ab-description" />
            </div>
          </div>

          {/* Ubicación */}
          <div className="ag-card p-6 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label>Dirección</Label>
                <Input value={form.address} onChange={onChange("address")} data-testid="ab-address" />
              </div>
              <div>
                <Label>Código postal</Label>
                <Input value={form.postal_code} onChange={onChange("postal_code")} />
              </div>
              <div>
                <Label>Ciudad *</Label>
                <Input value={form.city} onChange={onChange("city")} required data-testid="ab-city" />
              </div>
              <div>
                <Label>Provincia</Label>
                <Input value={form.province} onChange={onChange("province")} />
              </div>
            </div>
          </div>

          {/* Contacto */}
          <div className="ag-card p-6 space-y-5">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <Label>Persona de contacto</Label>
                <Input value={form.contact_name} onChange={onChange("contact_name")} />
              </div>
              <div>
                <Label>Email * (donde recibirás el link de confirmación)</Label>
                <Input type="email" value={form.email} onChange={onChange("email")} required
                  data-testid="ab-email" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={form.phone} onChange={onChange("phone")} placeholder="+34 …" />
              </div>
              <div>
                <Label>WhatsApp</Label>
                <Input value={form.whatsapp} onChange={onChange("whatsapp")} placeholder="+34 …" />
              </div>
              <div className="md:col-span-2">
                <Label>Sitio web</Label>
                <Input value={form.website_url} onChange={onChange("website_url")}
                  placeholder="https://" />
              </div>
            </div>
          </div>

          {/* Turnstile + submit */}
          <div className="ag-card p-6 space-y-4">
            <TurnstileWidget onToken={setTToken} />
            {error && <div className="text-sm text-red-400" data-testid="ab-error">{error}</div>}
            <button type="submit" disabled={submitting}
              className="ag-btn-gold w-full md:w-auto px-8 py-3.5 text-sm font-semibold tracking-wider uppercase"
              data-testid="ab-submit">
              {submitting ? "Enviando…" : "Enviar solicitud · gratis 30 días"}
            </button>
            <p className="text-[11px] text-[var(--ag-text-dim)]" data-testid="ab-legal-notice">
              Al continuar aceptas nuestra <a href="/privacidad" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)] hover:underline">Política de privacidad</a> y los <a href="/terminos" target="_blank" rel="noopener noreferrer" className="text-[var(--ag-gold)] hover:underline">Términos de servicio</a>. El perfil quedará pendiente de revisión por nuestro equipo antes de hacerse público.
            </p>
          </div>
        </form>
      </main>
    </div>
  );
}

const Label = ({ children }) => (
  <label className="block text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] mb-1.5">{children}</label>
);
const Input = (props) => (
  <input {...props}
    className={`w-full h-11 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none transition-colors ${props.className||""}`} />
);
const Textarea = (props) => (
  <textarea {...props}
    className={`w-full px-3 py-2.5 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm focus:border-[var(--ag-gold)]/60 outline-none transition-colors ${props.className||""}`} />
);
